import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { Search, Plus, Trash2, Clock, Edit, Loader2, FileText } from 'lucide-react';

interface PlacementNote {
    id: string;
    placement_id: string;
    author_name: string;
    content: string;
    created_at: string;
    updated_at: string;
}

interface PlacementNotesProps {
    placementId: string;
    currentUser: any;
}

export const PlacementNotes: React.FC<PlacementNotesProps> = ({ placementId, currentUser }) => {
    const [notes, setNotes] = useState<PlacementNote[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [configError, setConfigError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Editor State
    const [isEditing, setIsEditing] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [noteContent, setNoteContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [notePendingDelete, setNotePendingDelete] = useState<PlacementNote | null>(null);
    const [noteDeleteBusy, setNoteDeleteBusy] = useState(false);

    useEffect(() => {
        setNotePendingDelete(null);
        if (!placementId) return;
        fetchNotes();
    }, [placementId]);

    const fetchNotes = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('placement_notes')
                .select('*')
                .eq('placement_id', placementId)
                .order('created_at', { ascending: false });
                
            if (error) {
                console.warn(error);
                setNotes([]);
                if ((error as any).code === 'PGRST205' || String((error as any).message || '').includes("Could not find the table")) {
                    setConfigError(
                        'De tabel placement_notes ontbreekt in Supabase. Voer het script sql/create_placement_notes.sql (of setup_placement_notes.sql) uit in de SQL Editor, daarna Vernieuwen.'
                    );
                } else setConfigError(null);
            } else if (data) {
                setConfigError(null);
                setNotes(data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!noteContent.trim() || !currentUser?.id) return;
        setIsSaving(true);
        
        try {
            if (editingNoteId) {
                // Update
                const { error } = await supabase
                    .from('placement_notes')
                    .update({ content: noteContent, updated_at: new Date().toISOString() })
                    .eq('id', editingNoteId);
                if (error) throw error;
            } else {
                // Insert
                const { error } = await supabase
                    .from('placement_notes')
                    .insert({
                        placement_id: placementId,
                        author_id: currentUser.id,
                        author_name: currentUser.name || 'Onbekend',
                        content: noteContent
                    });
                if (error) throw error;
            }
            
            await fetchNotes();
            closeEditor();
        } catch (e: any) {
            alert(`Fout bij opslaan notitie: ${e.message || 'Onbekend rapport'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDeleteNote = async () => {
        if (!notePendingDelete) return;
        setNoteDeleteBusy(true);
        try {
            const { error } = await supabase.from('placement_notes').delete().eq('id', notePendingDelete.id);
            if (error) throw error;
            setNotes((prev) => prev.filter((n) => n.id !== notePendingDelete.id));
            setNotePendingDelete(null);
        } catch (e) {
            console.error(e);
            alert('Kon notitie niet verwijderen.');
        } finally {
            setNoteDeleteBusy(false);
        }
    };

    const openEditor = (note?: PlacementNote) => {
        if (note) {
            setEditingNoteId(note.id);
            setNoteContent(note.content);
        } else {
            setEditingNoteId(null);
            setNoteContent('');
        }
        setIsEditing(true);
    };

    const closeEditor = () => {
        setIsEditing(false);
        setEditingNoteId(null);
        setNoteContent('');
    };

    const safeFormatDate = (dateString: string) => {
        try { return new Date(dateString).toLocaleString('nl-NL'); }
        catch { return String(dateString); }
    };

    const filteredNotes = notes.filter(n => (n.content?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || (n.author_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()));

    if (!placementId) {
        return (
             <div className="p-12 text-center text-slate-500">
                 Sla de plaatsing eerst op voordat je notities kunt toevoegen.
             </div>
        );
    }

    return (
        <div className="w-full flex flex-col h-full bg-white relative rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 gap-4">
                <h2 className="text-[16px] font-black text-slate-800 flex items-center gap-2">
                    Notities <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">{notes.length}</span>
                </h2>
                {!isEditing && (
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input
                                type="text"
                                placeholder="Zoek notities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[12px] font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                            />
                        </div>
                        <button 
                            onClick={() => openEditor()} 
                            disabled={!!configError}
                            className="flex items-center gap-2 bg-[#e51b23] text-white px-4 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest hover:bg-[#B71C1C] transition-colors disabled:opacity-40 disabled:pointer-events-none"
                        >
                            <Plus size={14} /> Nieuwe Notitie
                        </button>
                    </div>
                )}
            </div>

            {configError ? (
                <div className="mx-6 mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-950 leading-snug">
                    {configError}
                </div>
            ) : null}

            {isEditing ? (
                <div className="p-5 bg-slate-50 flex flex-col gap-3 border-b border-slate-200">
                    <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                        {editingNoteId ? 'Notitie bewerken' : 'Nieuwe notitie'}
                    </div>
                    <textarea 
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        className="w-full h-32 p-3 text-[13px] border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 resize-none"
                        placeholder="Typ je notitie hier..."
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={closeEditor} className="px-4 py-2 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-200 rounded-lg transition-colors">Annuleren</button>
                        <button onClick={handleSave} disabled={isSaving || !noteContent.trim()} className="px-4 py-2 text-[11px] font-black uppercase tracking-widest bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2">
                            {isSaving ? <Loader2 size={14} className="animate-spin" /> : null} Opslaan
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="flex-1 overflow-y-auto p-0">
                {isLoading ? (
                    <div className="flex justify-center p-12"><Loader2 className="animate-spin text-slate-300" size={32} /></div>
                ) : configError ? (
                    <div className="p-8 text-center text-[13px] text-slate-600">Notities zijn uitgeschakeld tot de database is bijgewerkt (zie melding hierboven).</div>
                ) : filteredNotes.length === 0 ? (
                    <div className="p-12 text-center flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 mb-4"><Edit size={24} /></div>
                        <h3 className="text-sm font-bold text-slate-900 mb-1">Geen Notities Gevonden</h3>
                        <p className="text-xs text-slate-500 font-medium mb-6">Voeg notities toe om belangrijke informatie over deze plaatsing bij te houden.</p>
                        <button onClick={() => openEditor()} disabled={!!configError} className="flex items-center gap-2 bg-white border border-[#e51b23] text-[#e51b23] px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors disabled:opacity-40 disabled:pointer-events-none">Start met typen</button>
                    </div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm border-b border-slate-200">
                            <tr>
                                <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Datum</th>
                                <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Auteur</th>
                                <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400 w-1/2">Notitie</th>
                                <th className="py-3 px-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Acties</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredNotes.map(note => (
                                <tr key={note.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="py-4 px-6 align-top">
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                                            <Clock size={12} className="text-slate-300"/> {safeFormatDate(note.created_at)}
                                        </div>
                                    </td>
                                    <td className="py-4 px-6 align-top">
                                        <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                                            <div className="w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-[9px] uppercase">{(note.author_name || '?').charAt(0)}</div>
                                            {note.author_name || 'Onbekend'}
                                        </div>
                                    </td>
                                    <td className="py-4 px-6 align-top">
                                        <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                                    </td>
                                    <td className="py-4 px-6 align-top text-right">
                                        <div className="flex justify-end gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                            <button
                                                type="button"
                                                onClick={() => openEditor(note)}
                                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                title="Bewerken"
                                            >
                                                <Edit size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setNotePendingDelete(note)}
                                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                title="Verwijderen"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {notePendingDelete ? (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-fadeIn">
                        <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <FileText size={24} />
                        </div>
                        <h3 className="text-lg font-black text-center text-slate-900 mb-2">Notitie verwijderen?</h3>
                        <p className="text-sm text-center text-slate-500 mb-3">Dit kan niet ongedaan worden gemaakt.</p>
                        <p className="text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3 max-h-24 overflow-y-auto whitespace-pre-wrap mb-6">
                            {notePendingDelete.content || '(Leeg)'}
                        </p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setNotePendingDelete(null)}
                                disabled={noteDeleteBusy}
                                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                            >
                                Annuleren
                            </button>
                            <button
                                type="button"
                                onClick={() => void confirmDeleteNote()}
                                disabled={noteDeleteBusy}
                                className="flex-1 py-3 bg-[#e51b23] hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {noteDeleteBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                                Verwijderen
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
